import React, { useState } from 'react';
import { FaStar, FaRegStar, FaTimes, FaHeart } from 'react-icons/fa';
import { submitRating } from '../api/client';

const EMOJI_OPTIONS = [
  { id: 'loved', emoji: '😍', label: 'Loved it!', color: 'text-pink-500', bgColor: 'bg-pink-50', borderColor: 'border-pink-400' },
  { id: 'good', emoji: '👍', label: 'Good', color: 'text-green-500', bgColor: 'bg-green-50', borderColor: 'border-green-400' },
  { id: 'okay', emoji: '😐', label: 'Okay', color: 'text-yellow-500', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-400' },
  { id: 'poor', emoji: '👎', label: 'Needs work', color: 'text-red-500', bgColor: 'bg-red-50', borderColor: 'border-red-400' },
];

const RatingPromptModal = ({ isOpen, onClose, requestId, tripName, onRatingSubmitted, tripStartDate, tripEndDate }) => {
  const [selectedEmoji, setSelectedEmoji] = useState(null);
  const [starRating, setStarRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  // Calculate trip status
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }
    return new Date(dateStr);
  };

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const tripStart = parseLocalDate(tripStartDate);
  if (tripStart) tripStart.setHours(0, 0, 0, 0);

  const tripEnd = parseLocalDate(tripEndDate);
  if (tripEnd) tripEnd.setHours(0, 0, 0, 0);

  const tripIsUpcoming = tripStart && now < tripStart;
  const tripHasEnded = tripEnd && now > tripEnd;
  // During trip: not upcoming AND not ended

  // Dynamic content based on trip status
  const getHeaderText = () => {
    if (tripHasEnded) return 'How was your trip?';
    if (tripIsUpcoming) return 'How does this look?';
    return 'How is your trip going?';
  };

  const getQuickReactionLabel = () => {
    if (tripHasEnded) return 'Quick reaction';
    if (tripIsUpcoming) return 'First impression';
    return 'So far...';
  };

  const getPlaceholderText = () => {
    if (tripHasEnded) return 'Share your experience - highlights, tips for others...';
    if (tripIsUpcoming) return 'Any thoughts on this itinerary plan?';
    return 'How is the trip going? Any thoughts so far...';
  };

  const handleSubmit = async () => {
    if (!selectedEmoji && starRating === 0) return;

    try {
      setSubmitting(true);
      await submitRating(requestId, {
        emoji_reaction: selectedEmoji,
        rating: starRating > 0 ? starRating : null,
        comment: comment || null,
      });
      setSubmitted(true);
      setTimeout(() => {
        if (onRatingSubmitted) onRatingSubmitted();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error submitting rating:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    // Store in localStorage so we don't prompt again for this trip
    const skipped = JSON.parse(localStorage.getItem('skippedRatingPrompts') || '[]');
    if (!skipped.includes(requestId)) {
      skipped.push(requestId);
      localStorage.setItem('skippedRatingPrompts', JSON.stringify(skipped));
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleSkip}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-primary-500 via-primary-400 to-secondary-400 p-6 text-white relative">
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <FaTimes className="text-lg" />
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <FaHeart className="text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{getHeaderText()}</h2>
              <p className="text-white/80 text-sm">{tripName}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">🎉</span>
              </div>
              <h3 className="text-xl font-bold text-neutral-800 mb-2">Thank you!</h3>
              <p className="text-neutral-600">Your feedback helps us improve.</p>
            </div>
          ) : (
            <>
              {/* Emoji Selection */}
              <div className="mb-6">
                <p className="text-sm font-medium text-neutral-700 mb-3 text-center">
                  {getQuickReactionLabel()}
                </p>
                <div className="flex justify-center gap-3">
                  {EMOJI_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setSelectedEmoji(option.id)}
                      className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                        selectedEmoji === option.id
                          ? `${option.bgColor} ${option.borderColor}`
                          : 'bg-white border-neutral-300 hover:border-neutral-300'
                      }`}
                    >
                      <span className="text-3xl mb-1">{option.emoji}</span>
                      <span className={`text-xs font-medium ${
                        selectedEmoji === option.id ? option.color : 'text-neutral-500'
                      }`}>
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Star Rating */}
              <div className="mb-6">
                <p className="text-sm font-medium text-neutral-700 mb-3 text-center">
                  Star rating (optional)
                </p>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className="text-3xl transition-all duration-150 hover:scale-110"
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setStarRating(star)}
                    >
                      {(hoverRating || starRating) >= star ? (
                        <FaStar className="text-yellow-400 drop-shadow-sm" />
                      ) : (
                        <FaRegStar className="text-neutral-300" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Comment */}
              <div className="mb-6">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={getPlaceholderText()}
                  rows={2}
                  className="w-full px-4 py-3 border-2 border-neutral-300 rounded-xl focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500 resize-none text-sm"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleSkip}
                  className="flex-1 py-3 px-4 rounded-xl font-medium text-neutral-600 bg-neutral-100 hover:bg-neutral-200 transition-colors"
                >
                  Maybe Later
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || (!selectedEmoji && starRating === 0)}
                  className={`flex-1 py-3 px-4 rounded-xl font-semibold text-white transition-all duration-200 ${
                    submitting || (!selectedEmoji && starRating === 0)
                      ? 'bg-neutral-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>

              {/* Detailed feedback link */}
              <p className="text-center text-xs text-neutral-400 mt-4">
                Want to provide more detailed feedback? Check the "Rate Trip" tab after submitting.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RatingPromptModal;
